import type { ButtonHTMLAttributes, ReactNode } from 'react';

export function cx(...names: Array<string | false | null | undefined>) {
  return names.filter(Boolean).join(' ');
}

export function Button({ className, variant = 'primary', children, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger' }) {
  return <button className={cx('button', variant !== 'secondary' && `button--${variant}`, className)} {...props}>{children}</button>;
}

export function Page({ title, actions, children }: { title: string; actions?: ReactNode; children: ReactNode }) {
  return <div className="page">
    <header className="page-head"><h1>{title}</h1>{actions && <div className="actions">{actions}</div>}</header>
    {children}
  </div>;
}

export function Section({ title, action, children, className, id }: { title?: string; action?: ReactNode; children: ReactNode; className?: string; id?: string }) {
  return <section className={cx('section', className)} id={id}>
    {title && <header className="section-head"><h2>{title}</h2>{action}</header>}
    {children}
  </section>;
}

export function Stats({ label, children }: { label?: string; children: ReactNode }) {
  return <dl className="stats" aria-label={label}>{children}</dl>;
}

export function Stat({ label, value, detail, tone }: { label: string; value: ReactNode; detail?: ReactNode; tone?: 'pos' | 'neg' | 'warn' }) {
  return <div className={cx('stat', tone && `stat--${tone}`)}>
    <dt>{label}</dt>
    <dd>{value}</dd>
    {detail && <dd className="stat-detail">{detail}</dd>}
  </div>;
}

const GOOD = ['ok', 'healthy', 'active', 'operational', 'stable', 'completed', 'ready', 'current', 'verified', 'validada', 'conciliado', 'elegible', 'disponible', 'recomendable', 'linked', 'delivered', 'ingreso'];
const WARN = ['warning', 'degraded', 'deploying', 'reduced', 'end_of_life', 'expired', 'stale', 'pending', 'pendiente', 'deferred', 'bloqueado', 'ocupado', 'sin verificar', 'no rentable', 'medium', 'high'];
const BAD = ['critical', 'down', 'unavailable', 'error', 'cancelled', 'missing', 'authorization_required', 'missing_scope', 'gasto'];

export function statusTone(value: string) {
  const normalized = value.toLowerCase();
  return GOOD.includes(normalized) ? 'good' : WARN.includes(normalized) ? 'warn' : BAD.includes(normalized) ? 'bad' : 'neutral';
}

export function Status({ value, label }: { value: string; label?: string }) {
  return <span className={`status status--${statusTone(value)}`}>{label ?? humanize(value)}</span>;
}

export function LoadingState({ label = 'Cargando…' }: { label?: string }) {
  return <p className="state" role="status">{label}</p>;
}

export function ErrorState({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  return <div className="state state--error" role="alert">
    <div><strong>No se pudieron cargar los datos</strong>{error.message}</div>
    {onRetry && <Button variant="secondary" onClick={onRetry}>Reintentar</Button>}
  </div>;
}

export function EmptyState({ title, detail, action }: { title: string; detail?: string; action?: ReactNode }) {
  return <div className="state">
    <div><strong>{title}</strong>{detail}</div>
    {action}
  </div>;
}

export function Note({ children, tone = 'info' }: { children: ReactNode; tone?: 'info' | 'success' | 'error' | 'warn' }) {
  return <div role="status" className={cx('note', tone !== 'info' && `note--${tone}`)}>{children}</div>;
}

export function Progress({ value }: { value: number }) {
  const safe = Math.max(0, Math.min(100, value));
  return <div className="progress" role="img" aria-label={`${safe} %`}><span style={{ width: `${safe}%` }} /></div>;
}

export function Field({ label, hint, wide, children }: { label: string; hint?: string; wide?: boolean; children: ReactNode }) {
  return <label className={cx('field', wide && 'field--wide')}><span>{label}</span>{children}{hint && <small>{hint}</small>}</label>;
}

export function Assumptions({ items, title = 'Supuestos del cálculo' }: { items: string[]; title?: string }) {
  if (items.length === 0) return null;
  return <details><summary>{title}</summary><ul className="bullets">{items.map((item, index) => <li key={index}>{item}</li>)}</ul></details>;
}

export function Pagination({ page, total, size = 25, onChange }: { page: number; total: number; size?: number; onChange: (page: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / size));
  return <div className="pager">
    <span role="status">{total === 0 ? '0 resultados' : `${number(page * size + 1)}–${number(Math.min(total, (page + 1) * size))} de ${number(total)}`}</span>
    <div><Button type="button" variant="ghost" disabled={page === 0} onClick={() => onChange(page - 1)}>Anterior</Button><Button type="button" variant="ghost" disabled={page + 1 >= pages} onClick={() => onChange(page + 1)}>Siguiente</Button></div>
  </div>;
}

export const searchText = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

export function currency(value: number | null | undefined, maximumFractionDigits = 0) {
  if (value === null || value === undefined) return 'Sin datos';
  return `${new Intl.NumberFormat('es-ES', { maximumFractionDigits, useGrouping: 'always' }).format(value)} ISK`;
}

export function number(value: number | null | undefined) {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('es-ES', { useGrouping: 'always' }).format(value);
}

export function dateTime(value: string | null | undefined) {
  if (!value) return 'Sin sincronizar';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

export function humanize(value: string) {
  const labels: Record<string, string> = { linked: 'Conectado', delivered: 'Entregado', paused: 'Pausado', reverted: 'Revertido', manufacturing: 'Fabricación', research_material_efficiency: 'Investigación de materiales', research_time_efficiency: 'Investigación de tiempo', copying: 'Copias', unknown: 'Sin verificar', current: 'Vigente', stale: 'Caducado', pending: 'Pendiente', healthy: 'Operativo', ok: 'Operativo', unavailable: 'No disponible', degraded: 'Degradado', active: 'Activo', idle: 'Inactivo', expired: 'Finalizado', completed: 'Completado', ready: 'Listo', planning: 'Planificación', planned: 'Planificada', cancelled: 'Cancelado', deploying: 'Despliegue', operational: 'Operativo', verified: 'Verificado', low: 'Bajo', medium: 'Medio', high: 'Alto', stable: 'Estable', reduced: 'Reducida', critical: 'Crítica', end_of_life: 'Fin de vida' };
  if (labels[value.toLowerCase()]) return labels[value.toLowerCase()];
  return value.replaceAll('_', ' ').replace(/^./, (character) => character.toUpperCase());
}

const ACTIVITIES: Record<string, string> = { manufacturing: 'Manufacturing', '1': 'Manufacturing', 'me research': 'ME', researching_material_efficiency: 'ME', material_efficiency: 'ME', '4': 'ME', 'te research': 'TE', researching_time_efficiency: 'TE', time_efficiency: 'TE', '3': 'TE', copying: 'Copy', '5': 'Copy', invention: 'Invention', '8': 'Invention', reaction: 'Reaction', '9': 'Reaction', '11': 'Reaction' };

export function activityName(activity: string) {
  return ACTIVITIES[activity.toLowerCase()] ?? activity;
}
